import { Popover, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import React, { ReactNode } from "react";
import { SearchInput } from "./Common/SearchInput";
import { SecondaryButton } from "./Common/SecondaryButton";
import { callDebounced } from "@/utils/debounceUtils";

interface MultiSelectPopoverProps {
    title?: string;
    target: ReactNode;
    children: ReactNode;
    isVisible: boolean;
    selectedCount?: number | string;
    onApply?: () => void;
    onCancel: (e?: any) => void;
    onSearch?: (value: string) => void;
    className?: string;
    placement?: string;
    isLoading?: boolean;
    showSearch?: boolean; // TODO: Presence or absence of `onSearch` can determine this
    allowEmptySelection?: boolean;
    zIndex?: number;
    popoverParentId?: string;
    saveOnCheck?: boolean;
    childClassName?: string;
}

export const MultiSelectPopover = ({
    title,
    isVisible = false,
    placement = "bottom",
    selectedCount,
    className,
    onSearch,
    onApply,
    onCancel,
    isLoading = false,
    children,
    target,
    showSearch = true,
    allowEmptySelection = false,
    popoverParentId,
    saveOnCheck = false,
    childClassName,
    ...rest
}: MultiSelectPopoverProps) => {
    const handleChange = (searchResult: string) => (onSearch ? callDebounced(300, onSearch, searchResult) : null);
    const showHeader = title ?? showSearch;

    return (
        <span>
            <Popover
                placement={placement}
                visible={isVisible}
                className={clsx("ui-multi-select-popover w-100 !max-w-100", className)}
                appendTo={popoverParentId ? document.querySelector(`#${popoverParentId}`) : document.body}
                onClickOutside={onCancel}
                {...rest}
            >
                {target}

                <Popover.Content className="ui-popover-list-content p-0">
                    {isVisible ? (
                        <>
                            {showHeader && (
                                <div className="space-y-3 p-3">
                                    {title && (
                                        <div
                                            className="flex-grow text-lg font-semibold"
                                            data-testid="multi-select-popover-title"
                                        >
                                            {title}
                                        </div>
                                    )}

                                    {showSearch && (
                                        <div>
                                            <SearchInput onChange={handleChange} />
                                        </div>
                                    )}
                                </div>
                            )}

                            <div
                                className={clsx(
                                    { "border-t border-t-gray-light": showHeader },
                                    !saveOnCheck && "max-h-75 overflow-auto",
                                    childClassName,
                                )}
                            >
                                {children}
                            </div>

                            {!saveOnCheck && (
                                <div className="flex w-full flex-row-reverse space-x-2 space-x-reverse border-t border-gray-light p-3.5">
                                    <SubmitButton
                                        disabled={selectedCount === 0 && !allowEmptySelection}
                                        isLoading={isLoading}
                                        onClick={onApply}
                                    >
                                        Apply
                                    </SubmitButton>

                                    <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
                                </div>
                            )}
                        </>
                    ) : (
                        <div />
                    )}
                </Popover.Content>
            </Popover>
        </span>
    );
};

interface ItemProps {
    children: ReactNode;
    className?: string;
}

const Item = ({ children, className, ...rest }: ItemProps) => {
    return (
        <div className={clsx("flex w-full justify-between py-4 px-3.5", className)} {...rest}>
            {children}
        </div>
    );
};

MultiSelectPopover.Item = Item;

const Header = ({ children, className, ...rest }: ItemProps) => {
    return (
        <div className={clsx("flex w-full justify-between bg-gray-lighter px-3.5 py-2 font-bold", className)} {...rest}>
            {children}
        </div>
    );
};

MultiSelectPopover.Header = Header;
